/**
 * 1.8.2 (D-1.8.2-3) — a calendar line in words: its time, its category word (never a colour alone) and what it is.
 * "Housekeeping · 6 visits · Crew A/B", "Companion · Wellness walk · with Sam", "Events · Bingo night · Auditorium".
 */
import type { TFunction } from 'i18next';
import type { CalLine } from '@shared/house-calendar';
import { activityWords } from '@shared/volunteers';
import { HELP_WORDS } from '@shared/tech';
import { clockWithPeriod } from '@shared/dayplan';

type Lang = 'en' | 'es';
export const langOf = (l: string): Lang => (l.startsWith('es') ? 'es' : 'en');
const cap = (s: string) => (s ? s[0]!.toUpperCase() + s.slice(1) : s);

/** "9:30 AM", or "All day" for a whole-day line. */
export function timeWords(line: CalLine, t: TFunction, lang: Lang): string {
  return line.minutes === null ? t('cal182.allDay') : clockWithPeriod(line.minutes, lang);
}

/** What the line is, without its category word or time. */
export function whatWords(line: CalLine, t: TFunction, lang: Lang): string {
  const parts: string[] = [];
  switch (line.category) {
    case 'housekeeping':
      parts.push(t('cal182.visits', { count: line.visits ?? 0 }));
      if (line.proposed) parts.push(t('cal182.pencil', { count: line.proposed }));
      parts.push(line.crews === 2 ? t('cal182.crewsTwo') : t('cal182.crewsOne'));
      break;
    case 'companion':
    case 'reading':
      if (line.activity) parts.push(cap(activityWords(line.activity, line.trip ?? null, lang)));
      if (line.waiting) parts.push(t('cal182.waiting'));
      else if (line.volunteerFirst) parts.push(t('cal182.withVolunteer', { name: line.volunteerFirst }));
      if (line.tenantName) parts.push(t('cal182.forTenant', { name: line.tenantName }));
      break;
    case 'tech':
      if (line.help?.length) parts.push(line.help.map((h) => HELP_WORDS[h]?.[lang] ?? h).join(', '));
      if (line.tenantName) parts.push(t('cal182.forTenant', { name: line.tenantName }));
      break;
    case 'events':
      if (line.name) parts.push(line.name);
      if (line.place) parts.push(line.place);
      break;
    case 'closed':
      if (line.name) parts.push(line.name);
      parts.push(line.scope === 'housekeeping' ? t('cal182.closedHousekeeping') : t('cal182.closedEverything'));
      break;
    case 'holidays':
      if (line.name) parts.push(line.name);
      break;
  }
  return parts.join(' · ');
}

/** "Housekeeping · 6 visits · Crew A/B" — the category word first. */
export function lineWords(line: CalLine, t: TFunction, lang: Lang): string {
  const what = whatWords(line, t, lang);
  return what ? `${t(`cal182.cat.${line.category}`)} · ${what}` : t(`cal182.cat.${line.category}`);
}

/** The words of the link a line carries, from where it points. */
export function linkWords(href: string, t: TFunction): string {
  if (href === '/housekeeping') return t('cal182.open.today');
  if (href.startsWith('/housekeeping?day=')) return t('cal182.open.pastDay');
  if (href.startsWith('/schedule')) return t('cal182.open.schedule');
  if (href.startsWith('/volunteers/') || href.startsWith('/volunteers?v=')) return t('cal182.open.volunteer');
  if (href.startsWith('/companion')) return t('cal182.open.companion');
  if (href.startsWith('/reading')) return t('cal182.open.reading');
  if (href.startsWith('/volunteers')) return t('cal182.open.volunteers');
  if (href.startsWith('/tech/')) return t('cal182.open.ticket');
  if (href.startsWith('/events/')) return t('cal182.open.event');
  return t('cal182.open.settings');
}
